import Color from "color";
import { t } from "ttag";

import { color, staticVizOverrides } from "metabase/ui/colors";
import {
  type ComputedVisualizationSettings,
  GRAPH_AXIS_SETTINGS,
  GRAPH_DATA_SETTINGS,
  GRAPH_DISPLAY_VALUES_SETTINGS,
  GRAPH_GOAL_SETTINGS,
  TIMELINE_EVENTS_SETTINGS,
  TOOLTIP_SETTINGS,
  type VisualizationDefinition,
  getDefaultSize,
  getMinSize,
} from "metabase/viz-core";
import { isDimension, isMetric } from "metabase-lib/v1/types/utils/isa";

import { getCartesianChartDefinition } from "../CartesianChart/definition";

const WaterfallViz: Omit<VisualizationDefinition, "checkRenderable"> = {
  getUiName: () => t`Waterfall`,
  identifier: "waterfall",
  iconName: "waterfall",
  isSensible: ({ cols, rows }) => {
    const dimensionCount = cols.filter(
      (col) => isDimension(col) && !isMetric(col),
    ).length;
    const metricCount = cols.filter(isMetric).length;
    return (
      rows.length > 1 &&
      cols.length >= 2 &&
      dimensionCount === 1 &&
      metricCount > 0
    );
  },
  // eslint-disable-next-line ttag/no-module-declaration -- see metabase#55045
  noun: t`waterfall chart`,
  minSize: getMinSize("waterfall"),
  defaultSize: getDefaultSize("waterfall"),
  maxMetricsSupported: 1,
  maxDimensionsSupported: 1,
  supportsVisualizer: false,
  settings: {
    ...GRAPH_AXIS_SETTINGS,
    ...GRAPH_GOAL_SETTINGS,
    "waterfall.increase_color": {
      getSection: () => t`Display`,
      getProps: () => ({ title: t`Increase color` }),
      widget: "color",
      getDefault: () => color("accent1"),
    },
    "waterfall.decrease_color": {
      getSection: () => t`Display`,
      getProps: () => ({ title: t`Decrease color` }),
      widget: "color",
      getDefault: () => color("accent3"),
    },
    "waterfall.show_total": {
      getSection: () => t`Display`,
      // eslint-disable-next-line ttag/no-module-declaration -- see metabase#55045
      title: t`Show total`,
      widget: "toggle",
      getDefault: () => true,
      inline: true,
    },
    "waterfall.total_color": {
      getSection: () => t`Display`,
      getProps: () => ({ title: t`Total color` }),
      widget: "color",
      // Unfortunately, to get static viz to look right, we need to avoid using alpha colors here
      getDefault: () => Color(color("text-primary", staticVizOverrides)).hex(),
      getHidden: (_series: any, vizSettings: ComputedVisualizationSettings) =>
        vizSettings["waterfall.show_total"] !== true,
      readDependencies: ["waterfall.show_total"],
    },
    ...GRAPH_DISPLAY_VALUES_SETTINGS,
    ...GRAPH_DATA_SETTINGS,
    ...TOOLTIP_SETTINGS,
    ...TIMELINE_EVENTS_SETTINGS,
  },
};

export const WATERFALL_CHART_DEFINITION =
  getCartesianChartDefinition(WaterfallViz);
